import {ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {RootState} from "../../../app/store";
import {APIError} from "../../../services/HttpService";
import {bulkDeleteConfirm, bulkDeletePreview, BulkDeletePreviewResult, cacheClearJobStatus} from "../../../services/UrlsService";
import {createDefaultMatchers, SliceStatus} from "../../BaseState";
import {RejectError, RejectErrorType} from "../../error/Error";
import {CacheClearJobStatus} from "../deleteUrls/DeleteUrlsState";
import BulkDeleteUrlsState, {BulkDeleteClientError} from "./BulkDeleteUrlsState";

const initialState: BulkDeleteUrlsState = {
  clientError: null,
  preview: null,
  previewError: null,
  confirmError: null,
  jobStatus: CacheClearJobStatus.idle,
  deletedCount: undefined,
  pendings: [],
  status: SliceStatus.idle,
  error: null
};

interface PreviewBulkDeleteAction {
  readonly userId: number;
  readonly file: File;
}

export const previewBulkDelete = createAsyncThunk<BulkDeletePreviewResult, PreviewBulkDeleteAction, {rejectValue: RejectError}>(
  "users/urls/bulkDelete/preview",
  async ({userId, file}: PreviewBulkDeleteAction, {rejectWithValue}) => {
    try {
      return await bulkDeletePreview(userId, file);
    } catch (error) {
      if (error instanceof APIError) return rejectWithValue({message: error.message, code: error.statusCode, type: RejectErrorType.API});

      throw error;
    }
  }
);

interface ConfirmBulkDeleteAction {
  readonly userId: number;
  readonly file: File;
  readonly contentHash: string;
}

export const confirmBulkDelete = createAsyncThunk<CacheClearJobStatus, ConfirmBulkDeleteAction, {rejectValue: RejectError}>(
  "users/urls/bulkDelete/confirm",
  async ({userId, file, contentHash}: ConfirmBulkDeleteAction, {rejectWithValue}) => {
    try {
      const result = await bulkDeleteConfirm(userId, file, contentHash);
      return result.status;
    } catch (error) {
      if (error instanceof APIError) return rejectWithValue({message: error.message, code: error.statusCode, type: RejectErrorType.API});

      throw error;
    }
  }
);

interface GetBulkDeleteJobStatusAction {
  readonly userId: number;
}

interface BulkDeleteJobStatusPayload {
  readonly status: CacheClearJobStatus;
  readonly deletedCount?: number;
}

export const getBulkDeleteJobStatus = createAsyncThunk<BulkDeleteJobStatusPayload, GetBulkDeleteJobStatusAction, {rejectValue: RejectError}>(
  "users/urls/bulkDelete/status",
  async ({userId}: GetBulkDeleteJobStatusAction, {rejectWithValue}) => {
    try {
      const result = await cacheClearJobStatus(userId);
      return {status: result.status, deletedCount: result.deletedCount};
    } catch (error) {
      if (error instanceof APIError) return rejectWithValue({message: error.message, code: error.statusCode, type: RejectErrorType.API});

      throw error;
    }
  }
);

const createActions = (builder: ActionReducerMapBuilder<BulkDeleteUrlsState>) => {
  builder.addCase(previewBulkDelete.fulfilled, (draft, action) => {
    draft.preview = action.payload;
    draft.previewError = null;
    draft.clientError = null;
  });
  builder.addCase(previewBulkDelete.rejected, (draft, action) => {
    if (action.payload) draft.previewError = action.payload;
  });
  builder.addCase(confirmBulkDelete.fulfilled, (draft, action) => {
    draft.jobStatus = action.payload;
    draft.confirmError = null;
  });
  builder.addCase(confirmBulkDelete.rejected, (draft, action) => {
    if (action.payload) draft.confirmError = action.payload;
  });
  builder.addCase(getBulkDeleteJobStatus.fulfilled, (draft, action) => {
    const {status, deletedCount} = action.payload;

    const jobEvictedWhileProcessing =
      status === CacheClearJobStatus.idle && draft.jobStatus === CacheClearJobStatus.processing;
    if (jobEvictedWhileProcessing) {
      draft.jobStatus = CacheClearJobStatus.completed;
      draft.deletedCount = undefined;
      return;
    }

    draft.jobStatus = status;
    draft.deletedCount = deletedCount;
  });

  createDefaultMatchers(
    [previewBulkDelete.typePrefix, confirmBulkDelete.typePrefix, getBulkDeleteJobStatus.typePrefix],
    builder
  );
};

export const bulkDeleteUrlsSlice = createSlice({
  name: "bulkDeleteUrls",
  initialState,
  reducers: {
    setClientError: (draft, action: PayloadAction<BulkDeleteClientError | null>) => {
      draft.clientError = action.payload;
    },
    resetToUpload: (draft) => {
      draft.preview = null;
      draft.previewError = null;
      draft.confirmError = null;
      draft.clientError = null;
      draft.jobStatus = CacheClearJobStatus.idle;
      draft.deletedCount = undefined;
    }
  },
  extraReducers: createActions
});

export const bulkDeleteUrlsState = (state: RootState): BulkDeleteUrlsState => state.bulkDeleteUrls;
export const {setClientError, resetToUpload} = bulkDeleteUrlsSlice.actions;
export default bulkDeleteUrlsSlice.reducer;
